import React, { useState, useEffect } from 'react';
import './Home.css';
import TextInput from '../components/TextInput';
import AIGauge from '../components/AIGauge';
import ResultModal from '../components/ResultModal';
import AuthModal from '../components/AuthModal';
import EmailVerificationModal from '../components/EmailVerificationModal';
import StepsSection from '../components/StepsSection';
import CTASection from '../components/CTASection';
import { useAuth } from '../contexts/AuthContext';
import { analyzeText, humanizeText, analyzeTextDemo, humanizeTextDemo } from '../utils/api';

interface DetectorScore {
  name: string;
  score: number;
  passed: boolean;
}

const Home: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [inputText, setInputText] = useState('');
  const [aiScore, setAiScore] = useState<number | null>(null);
  const [detectorScores, setDetectorScores] = useState<DetectorScore[]>([]);
  const [humanizedText, setHumanizedText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isHumanizing, setIsHumanizing] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [hasUsedFreeCheck, setHasUsedFreeCheck] = useState(() => {
    return localStorage.getItem('hasUsedFreeAICheck') === 'true';
  });

  // Reset free check limit when user logs in
  useEffect(() => {
    if (isAuthenticated && hasUsedFreeCheck) {
      setHasUsedFreeCheck(false);
      localStorage.removeItem('hasUsedFreeAICheck');
    }
  }, [isAuthenticated, hasUsedFreeCheck]);

  const checkForAI = async () => {
    if (!inputText.trim()) return;
    
    // Check if user is not authenticated and has already used their free check
    if (!isAuthenticated && hasUsedFreeCheck) {
      setShowAuthModal(true);
      return;
    }
    
    setIsAnalyzing(true);
    setShowResultModal(true);
    
    try {
      // Use real API in production, demo in development
      const isDevelopment = process.env.NODE_ENV === 'development';
      const result = isDevelopment
        ? await analyzeTextDemo(inputText)
        : await analyzeText(inputText);

      setAiScore(result.aiScore);
      setDetectorScores(result.detectorScores || []);

      // Mark free check as used for non-authenticated users
      if (!isAuthenticated) {
        setHasUsedFreeCheck(true);
        localStorage.setItem('hasUsedFreeAICheck', 'true');
      }
    } catch (error) {
      console.error('Error analyzing text:', error);
      // Fallback to demo on error
      const result = await analyzeTextDemo(inputText);
      setAiScore(result.aiScore);
      setDetectorScores(result.detectorScores || []);

      // Mark free check as used for non-authenticated users
      if (!isAuthenticated) {
        setHasUsedFreeCheck(true);
        localStorage.setItem('hasUsedFreeAICheck', 'true');
      }
    }
    
    setIsAnalyzing(false);
  };

  const humanizeTextAction = async () => {
    if (!inputText.trim()) return;

    // Check if user is authenticated
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    setIsHumanizing(true);

    try {
      // Use real API in production, demo in development
      const isDevelopment = process.env.NODE_ENV === 'development';
      const result = isDevelopment
        ? await humanizeTextDemo(inputText)
        : await humanizeText(inputText);

      setHumanizedText(result.humanizedText);

      // Re-analyze the humanized text to get new scores
      const analysis = await analyzeTextDemo(result.humanizedText);
      setAiScore(analysis.aiScore);
      setDetectorScores(analysis.detectorScores || []);
    } catch (error) {
      console.error('Error humanizing text:', error);
      // Fallback to demo on error
      const result = await humanizeTextDemo(inputText);
      setHumanizedText(result.humanizedText);

      // Re-analyze the humanized text
      const analysis = await analyzeTextDemo(result.humanizedText);
      setAiScore(analysis.aiScore);
      setDetectorScores(analysis.detectorScores || []);
    }

    setIsHumanizing(false);
  };

  const handleEmailSent = (email: string) => {
    setVerificationEmail(email);
    setShowAuthModal(false);
    setShowEmailVerification(true);
  };

  const scrollToWorkspace = () => {
    const workspace = document.getElementById('workspace');
    if (workspace) {
      workspace.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="home">
      <main className="main">
        <div className="hero">
          <div className="trust-badge">FERRAMENTA PROFISSIONAL DE HUMANIZAÇÃO</div>
          <h1 className="title">Transforme Texto de IA em Conteúdo Natural e Humanizado</h1>
          <p className="subtitle">
            O Humanizador aprimora seu conteúdo gerado por IA, tornando-o mais natural e autêntico,
            <br />
            com qualidade profissional que se destaca em todas as plataformas
          </p>
          <button className="cta-button" onClick={scrollToWorkspace}>Experimente grátis</button>
          <p className="no-credit">Não é necessário cartão de crédito</p>
        </div>

        <div id="workspace" className="workspace">
          <TextInput 
            value={inputText}
            onChange={setInputText}
            placeholder="Cole seu texto aqui..."
          />
          
          <div className="action-buttons">
            <button 
              className="check-ai-btn"
              onClick={checkForAI}
              disabled={!inputText.trim() || isAnalyzing}
              title={!isAuthenticated && hasUsedFreeCheck ? 'Faça login para mais verificações' : ''}
            >
              {isAnalyzing ? 'Analisando...' : 
               (!isAuthenticated && hasUsedFreeCheck ? 'Login para Verificar' : 'Verificar IA')}
            </button>
            
            <button
              className="humanize-btn"
              onClick={humanizeTextAction}
              disabled={!inputText.trim() || isHumanizing}
            >
              {isHumanizing ? 'Humanizando...' : 'Humanizar'}
            </button>
          </div>

          {aiScore !== null && !showResultModal && (
            <AIGauge score={aiScore} />
          )}

          {humanizedText && (
            <div className="result">
              <h3>Texto Humanizado:</h3>
              <div className="result-text">
                {humanizedText}
              </div>
            </div>
          )}
        </div>

        <div className="bypass-section">
          <h2 className="bypass-title">Compatível com as principais plataformas de análise de conteúdo</h2>
          <div className="detector-logos">
            <div className="detector-logo">Turnitin</div>
            <div className="detector-logo">Copyleaks</div>
            <div className="detector-logo">ZeroGPT</div>
            <div className="detector-logo">QuillBot</div>
            <div className="detector-logo">Grammarly</div>
            <div className="detector-logo">GPTZero</div>
          </div>
        </div>
      </main>

      <ResultModal
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
        aiScore={aiScore}
        isAnalyzing={isAnalyzing}
        detectorScores={detectorScores}
      />
      
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onEmailSent={handleEmailSent}
      />
      
      <EmailVerificationModal 
        isOpen={showEmailVerification}
        onClose={() => setShowEmailVerification(false)}
        email={verificationEmail}
      />

      <StepsSection />
      <CTASection onTryFree={scrollToWorkspace} />
    </div>
  );
};

export default Home;